import type { App } from "vue";
import axios from "axios";
import JwtService from "@/core/services/JwtService";
import { ApiClient } from "@/core/services/ApiClient";
import { currentOrganization } from "@/core/services/OrganizationService";
import moment from "moment";
import {
  hasJwtToken,
  isAccountAuthRoute,
  isValidJwtToken,
  shouldSkipAuthRetry,
} from "@/core/helpers/auth-token";
import { refreshAccessTokenOnce } from "@/core/helpers/refresh-access-token";

class ApiService {
  public static apiClient: ApiClient;
  public static isInited: boolean;

  private static isValidDateString(value: any): boolean {
    if (typeof value !== "string") {
      return false;
    }
    // Kiểm tra các định dạng ngày tháng phổ biến
    const formats = [
      moment.ISO_8601,
      "YYYY-MM-DD",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DDTHH:mm:ssZ",
      "DD-MM-YYYY",
      "DD/MM/YYYY",
      "MM/DD/YYYY",
      // Thêm các định dạng khác nếu cần
    ];
    return moment(value, formats, true).isValid();
  }
  private static convertDatesToISOString(data: any | null | undefined): any {
    if (Array.isArray(data)) {
      return data.map((item) => this.convertDatesToISOString(item));
    } else if (typeof data === "object" && data !== null) {
      const result: any = { ...data };

      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          const value = data[key];
          if (key == "startDate") {
          }
          if (!value) result[key] = value;
          else if (Array.isArray(value) || typeof value === "object") {
            result[key] = this.convertDatesToISOString(value);
          } else if (this.isValidDateString(value)) {
            result[key] = moment(value).format("YYYY-MM-DDTHH:mm:ss");
          }
        }
      }
      return result;
    }
    return data;
  }
  private static convertDatesToISO(data: any | null | undefined): any {
    if (Array.isArray(data)) {
      return data.map((item) => this.convertDatesToISO(item));
    } else if (typeof data === "object" && data !== null) {
      const result: any = { ...data };

      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          const value = data[key];
          if (key == "startDate") {
          }
          if (!value) result[key] = value;
          else if (Array.isArray(value) || typeof value === "object") {
            result[key] = this.convertDatesToISO(value);
          } else if (this.isValidDateString(value)) {
            result[key] = moment(value).utcOffset(0);
          }
        }
      }
      return result;
    }
    return data;
  }

  public static init(app: App<Element>) {
    if (this.isInited) return;
    console.log("init api service");
    let isRedirectingToLogin = false;
    const redirectToLogin = () => {
      if (typeof window === "undefined") return;
      if (isAccountAuthRoute(window.location.pathname)) return;
      if (isRedirectingToLogin) return;
      isRedirectingToLogin = true;
      JwtService.destroyToken();
      window.location.href = "/account/login";
    };
    const syncAuthToken = (token: string) => {
      try {
        const authStore = useAuthStore();
        authStore.token = token;
        authStore.isAuthenticated = true;
      } catch {
        /* pinia chưa sẵn sàng */
      }
    };
    // config default axios
    const config = useRuntimeConfig();
    const axiosInstance = axios.create({
      baseURL: config.public.apiBaseUrl,
      withCredentials: true,
    });
    axios.defaults.withCredentials = true;
    axiosInstance.defaults.withCredentials = true;
    axiosInstance.interceptors.request.use(
      async (config) => {
        const url = typeof config.url === "string" ? config.url : "";
        let token = JwtService.getToken();
        if (!shouldSkipAuthRetry(url) && hasJwtToken(token) && !isValidJwtToken(token) && this.apiClient) {
          const refreshed = await refreshAccessTokenOnce(() => this.apiClient.refreshToken());
          if (refreshed) {
            token = refreshed;
            syncAuthToken(refreshed);
          }
        }
        if (isValidJwtToken(token)) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        config.headers["X-App"] = "cms";
        config.headers["X-ObjectId"] = String(currentOrganization());
        config.withCredentials = true;

        if (
          config.method?.toLowerCase() === "post" ||
          config.method?.toLowerCase() === "put"
        ) {
          const url = typeof config.url === "string" ? config.url : "";
          if (
            url.includes("forgot-password") ||
            url.includes("check-fogot-password-otp")
          ) {
            return config;
          }
          // Duyệt qua tất cả các thuộc tính của dữ liệu trong yêu cầu
          try {
            config.data = JSON.stringify(
              this.convertDatesToISOString(JSON.parse(config.data))
            );
          } catch (error) {}
        }

        return config;
      },
      (error) => {
        // Handle request error
        return Promise.reject(error);
      }
    );
    axiosInstance.interceptors.response.use(
      (response) => {
        // Handle successful response
        if (response.data) {
          try {
            // response.data = this.convertDatesToISO(response.data);
          } catch (error) {
            console.log(error);
          }
        }
        return response;
      },
      (error) => {
        const originalRequest = error.config;
        const requestUrl = typeof originalRequest?.url === "string" ? originalRequest.url : "";
        if (shouldSkipAuthRetry(requestUrl)) {
          return Promise.reject(error);
        }
        if (
          error.response &&
          error.response.status === 401 &&
          !originalRequest._retry
        ) {
          const currentToken = JwtService.getToken();
          if (!hasJwtToken(currentToken)) {
            return Promise.reject(error);
          }
          originalRequest._retry = true;
          return refreshAccessTokenOnce(() => this.apiClient.refreshToken()).then((token) => {
            if (!token) {
              redirectToLogin();
              return Promise.reject(error);
            }
            syncAuthToken(token);
            originalRequest.headers["Authorization"] = "Bearer " + token;
            return axiosInstance(originalRequest);
          });
        } else {
          return Promise.reject(error);
        }
      }
    );
    this.apiClient = new ApiClient(config.public.apiBaseUrl, axiosInstance);
    this.isInited = true;
  }
}

export default ApiService;
